import { lazy, memo, Suspense, useEffect, useState } from "react";
import { MobileNavMenu } from "./MobileNavMenu";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import logo from "@/assets/klarfinans_logo_ny.webp";
import {
  BusinessLoanPage,
  ContactPage,
  KlarsprakPage,
  LoanWithoutUCPage,
  MortgagePage,
  PrivateLoanPage,
} from "@/routes/preloadableRoutes";

const DESKTOP_NAVIGATION_QUERY = "(min-width: 1024px)";

const DesktopNavigation = lazy(() =>
  import("./DesktopNavigation").then((module) => ({ default: module.DesktopNavigation }))
);

const warmMainRoutes = () => {
  void PrivateLoanPage.preload();
  void MortgagePage.preload();
  void LoanWithoutUCPage.preload();
  void BusinessLoanPage.preload();
  void KlarsprakPage.preload();
  void ContactPage.preload();
};

const getInitialDesktopNavigationState = () =>
  typeof window !== "undefined" && window.matchMedia(DESKTOP_NAVIGATION_QUERY).matches;

const useShouldRenderDesktopNavigation = () => {
  const [shouldRenderDesktopNavigation, setShouldRenderDesktopNavigation] = useState(
    getInitialDesktopNavigationState
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_NAVIGATION_QUERY);
    const updateDesktopNavigationState = () => {
      setShouldRenderDesktopNavigation(mediaQuery.matches);
    };

    updateDesktopNavigationState();
    mediaQuery.addEventListener("change", updateDesktopNavigationState);

    return () => mediaQuery.removeEventListener("change", updateDesktopNavigationState);
  }, []);

  return shouldRenderDesktopNavigation;
};

const Navigation = () => {
  const { currentLanguage } = useLanguage();
  const shouldRenderDesktopNavigation = useShouldRenderDesktopNavigation();

  useEffect(() => {
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    const schedule = () => {
      if (typeof window.requestIdleCallback === "function") {
        idleId = window.requestIdleCallback(warmMainRoutes, { timeout: 3000 });
      } else {
        timeoutId = window.setTimeout(warmMainRoutes, 1500);
      }
    };
    const startId = window.setTimeout(schedule, 800);
    return () => {
      window.clearTimeout(startId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      if (idleId !== undefined && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
    };
  }, []);

  return (
    <nav 
      className="fixed left-0 right-0 top-0 z-50 border-b border-border bg-background shadow-sm"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-14 md:h-16">
          {/* Logo */}
          <Link to={`/${currentLanguage}`} className="flex items-center">
            <img 
              src={logo}
              srcSet={`${logo} 500w`}
              sizes="(max-width: 768px) 120px, 150px"
              alt="Klarfinans - Jämför lån och bolån"
              className="h-10 md:h-12 w-auto transition-opacity hover:opacity-90 active:opacity-80"
              width="150"
              height="51"
              loading="eager"
              decoding="async"
            />
          </Link>

          {shouldRenderDesktopNavigation && (
            <Suspense fallback={null}>
              <DesktopNavigation />
            </Suspense>
          )}

          {/* Mobile Menu */}
          <MobileNavMenu />
        </div>

      </div>
    </nav>
  );
};

export default memo(Navigation);
